import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import {
  Search,
  ShieldCheck,
  ShieldOff,
  UserCog,
  Trash2,
  Plus,
  Info,
  Lock,
  AlertCircle
} from 'lucide-react';
import ConfirmDialog from './ConfirmDialog';
import { Toast } from './ui/Toast';
import { api } from '../services/api';
import { usePermissions } from '../hooks/usePermissions';
import { useFeatures } from '../contexts/FeaturesContext';
import { clesLicence } from '../config/licenceModules';

/**
 * ====================================
 * Dérogations individuelles
 * ====================================
 *
 * Le modèle de droits attribue par groupe (les portes de module) et par rôle
 * (les actions fines). Il ne sait pas dire « tout le groupe sauf lui », ni
 * « lui seul, exceptionnellement ». C'est ce que cet écran ajoute.
 *
 *   accorder — ouvre une permission que ni le groupe ni le rôle ne donnent
 *   refuser  — ferme une permission que le groupe ou le rôle donne
 *
 * Un refus l'emporte sur tout. C'est le seul moyen de retirer un droit à une
 * personne sans le retirer à tout son groupe.
 *
 * ⚠️ Une dérogation n'expire pas. C'est un écart durable à la règle, pas une
 * autorisation temporaire : l'écran le dit, et le motif est obligatoire pour
 * qu'on sache dans six mois pourquoi elle a été posée.
 */

const LIBELLE_TYPE = {
  accorder: 'Accordée',
  refuser: 'Refusée'
};

// Les deux boutons d'un pied de modal ont la meme taille ET la meme largeur :
// un bouton d'annulation plus petit suggere une hierarchie qui n'existe pas.
// DESIGN-SYSTEM.md, section Boutons.
const BOUTON_PIED_CLASSES = 'h-9 min-w-[168px] justify-center gap-2';

/**
 * Un employe a-t-il un acces a l'application ?
 *
 * `info_pro_acces_application` est LE prerequis : c'est la colonne qu'on met a
 * 1 quand une demande d'acces est approuvee (EmployeController), et elle vaut
 * 0 par defaut. Elle dit « un compte lui est ouvert », qu'il s'y soit connecte
 * ou non — `last_login` ne rentre pas en ligne de compte.
 *
 * Deux revocations s'y soustraient, parce qu'elles bloquent reellement :
 *   · `acces_suspendu` — `verifyToken` refuse CHAQUE requete d'un suspendu
 *   · `statut` — la connexion cherche `WHERE identifiant = ? AND statut = "actif"`
 *
 * `acces_suspendu` manque encore sur certaines bases (voir auth.js) : absente,
 * on la lit comme « non suspendu » plutot que de reléguer tout le monde.
 *
 * Ce qui etait teste avant — `identifiant || email` — ne separait presque
 * personne : 69 employes sur 70 ont une adresse, et l'email n'authentifie
 * rien. Le vrai compte est de 5 sur 70.
 */
const aUnAccesApplication = (e) =>
  Number(e?.info_pro_acces_application) === 1
  && Number(e?.acces_suspendu || 0) !== 1
  && String(e?.statut || 'actif').toLowerCase() === 'actif';

// `created_at` est un horodatage complet : on n'affiche que le jour, c'est la
// seule part qui compte pour situer un geste dans un tableau.
const joliJour = (valeur) => {
  if (!valeur) return null;
  const jour = String(valeur).slice(0, 10);
  const [a, m, j] = jour.split('-');
  return a && m && j ? `${j}/${m}/${a}` : null;
};

const DerogationsManager = () => {
  const { auMoinsUne } = useFeatures();

  // Une dérogation posée sur soi-même doit se voir tout de suite. Le serveur
  // vide son propre cache ; celui du navigateur, lui, garde les permissions
  // cinq minutes — sans ce rafraîchissement, le menu mettrait ce délai à
  // refléter un droit qu'on vient de retirer.
  const { refreshPermissions } = usePermissions();

  const [employes, setEmployes] = useState([]);
  // Masques par defaut : une derogation ne produit rien pour eux.
  const [montrerSansAcces, setMontrerSansAcces] = useState(false);
  const [derogationARetirer, setDerogationARetirer] = useState(null);
  const [retraitEnCours, setRetraitEnCours] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [permissions, setPermissions] = useState([]);
  const [recherche, setRecherche] = useState('');
  const [employeChoisi, setEmployeChoisi] = useState(null);
  const [detail, setDetail] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [chargementDetail, setChargementDetail] = useState(false);
  const [erreur, setErreur] = useState(null);


  const avertir = (message, type = 'success') => setToast({ show: true, message, type });

  /**
   * Mes propres droits n'ont change que si la derogation me concerne.
   *
   * `refreshPermissions()` recharge les permissions du connecte (sans plus
   * repasser le contexte en chargement depuis le 18 septembre 2026). L'appeler
   * pour la derogation d'un collegue reste un appel serveur pour rien.
   */
  const rafraichirSiCestMoi = useCallback(async (employeId) => {
    let moi = null;
    try {
      moi = JSON.parse(localStorage.getItem('user') || '{}')?.id;
    } catch {
      moi = null;
    }
    if (moi && Number(moi) === Number(employeId)) {
      await refreshPermissions();
    }
  }, [refreshPermissions]);

  // Dialogue d'ajout
  const [dialogueOuvert, setDialogueOuvert] = useState(false);
  const [cleChoisie, setCleChoisie] = useState('');
  const [typeChoisi, setTypeChoisi] = useState('accorder');
  const [motif, setMotif] = useState('');
  const [rechercheCle, setRechercheCle] = useState('');
  const [enregistrement, setEnregistrement] = useState(false);

  // ---------------------------------------------------------------- chargement

  useEffect(() => {
    const charger = async () => {
      try {
        setChargement(true);
        const [reponseEmployes, reponsePermissions] = await Promise.all([
          api.employes.getAll(),
          api.permissions.getAll()
        ]);

        if (reponseEmployes?.success && Array.isArray(reponseEmployes.data)) {
          setEmployes(reponseEmployes.data);
        }
        if (reponsePermissions?.success && Array.isArray(reponsePermissions.data)) {
          setPermissions(reponsePermissions.data);
        }
        setErreur(null);
      } catch (e) {
        setErreur(e.message);
      } finally {
        setChargement(false);
      }
    };
    charger();
  }, []);

  const chargerDetail = useCallback(async (employeId) => {
    try {
      setChargementDetail(true);
      const reponse = await api.permissions.getDerogations(employeId);
      if (reponse?.success) {
        setDetail(reponse.data);
        setErreur(null);
      } else {
        setErreur(reponse?.message || 'Lecture des dérogations impossible');
        setDetail(null);
      }
    } catch (e) {
      setErreur(e.message);
      setDetail(null);
    } finally {
      setChargementDetail(false);
    }
  }, []);

  const choisirEmploye = (employe) => {
    setEmployeChoisi(employe);
    setDetail(null);
    chargerDetail(employe.id);
  };

  // ---------------------------------------------------------------- dérivés

  // Seuls les employés qui ont un accès voient l'effet d'une dérogation.
  //
  // Ils étaient tous affichés, les sans-accès relégués en bas — « pour pouvoir
  // préparer les droits avant d'ouvrir un compte ». Sur csap1 cela fait 63
  // lignes sur 70 pour lesquelles une dérogation ne produit rien : on cherchait
  // les sept qui comptent au milieu du reste.
  //
  // Ils sont donc masqués par défaut, et l'interrupteur garde le cas d'usage
  // d'origine en annonçant combien de personnes il révèle.
  const employesTries = useMemo(() => {
    const terme = recherche.trim().toLowerCase();
    const correspond = (e) => {
      if (!terme) return true;
      return `${e.prenom || ''} ${e.nom || ''} ${e.email || ''}`.toLowerCase().includes(terme);
    };
    const peutSeConnecter = aUnAccesApplication;
    return employes
      .filter((e) => (montrerSansAcces || peutSeConnecter(e)) && correspond(e))
      .sort((a, b) => {
        const ca = peutSeConnecter(a) ? 0 : 1;
        const cb = peutSeConnecter(b) ? 0 : 1;
        if (ca !== cb) return ca - cb;
        return `${a.nom || ''}`.localeCompare(`${b.nom || ''}`, 'fr');
      });
  }, [employes, recherche, montrerSansAcces]);

  const nombreConnectables = useMemo(
    () => employes.filter(aUnAccesApplication).length,
    [employes]
  );

  const acquises = useMemo(
    () => new Set((detail?.acquis || []).map((a) => a.key)),
    [detail]
  );

  const derogeesParCle = useMemo(
    () => new Map((detail?.derogations || []).map((d) => [d.key, d])),
    [detail]
  );

  // Une dérogation sur un module que l'école n'a pas souscrit n'ouvrirait
  // rien : `requireFeature` refuserait la requête avant même d'examiner les
  // droits. On ne la propose donc pas.
  const permissionsProposables = useMemo(() => {
    const terme = rechercheCle.trim().toLowerCase();
    return permissions
      .filter((p) => {
        if (!p.key) return false;
        if (derogeesParCle.has(p.key)) return false;
        const cles = clesLicence(p.module);
        if (cles.length > 0 && !auMoinsUne(cles)) return false;
        if (!terme) return true;
        return `${p.key} ${p.description || ''}`.toLowerCase().includes(terme);
      })
      .sort((a, b) => a.key.localeCompare(b.key))
      .slice(0, 80);
  }, [permissions, rechercheCle, derogeesParCle, auMoinsUne]);

  // ---------------------------------------------------------------- actions

  const enregistrer = async () => {
    if (!employeChoisi || !cleChoisie) return;
    if (motif.trim().length < 5) {
      setErreur('Le motif doit faire au moins 5 caractères.');
      return;
    }

    try {
      setEnregistrement(true);
      const reponse = await api.permissions.setDerogation(employeChoisi.id, {
        permissionKey: cleChoisie,
        type: typeChoisi,
        raison: motif.trim()
      });

      if (reponse?.success) {
        avertir(reponse.message || 'Dérogation posée');
        setDialogueOuvert(false);
        setCleChoisie('');
        setMotif('');
        setRechercheCle('');
        await chargerDetail(employeChoisi.id);
        await rafraichirSiCestMoi(employeChoisi.id);
      } else {
        avertir(reponse?.message || "Enregistrement impossible", 'error');
      }
    } catch (e) {
      avertir(e.message, 'error');
    } finally {
      setEnregistrement(false);
    }
  };

  const retirer = async () => {
    if (!employeChoisi || !derogationARetirer) return;
    setRetraitEnCours(true);
    try {
      const reponse = await api.permissions.removeDerogation(
        employeChoisi.id, derogationARetirer.permission_id ?? derogationARetirer.id
      );
      if (reponse?.success) {
        avertir('Dérogation retirée');
        setDerogationARetirer(null);
        await chargerDetail(employeChoisi.id);
        await rafraichirSiCestMoi(employeChoisi.id);
      } else {
        avertir(reponse?.message || 'Retrait impossible', 'error');
      }
    } catch (e) {
      avertir(e.message, 'error');
    } finally {
      setRetraitEnCours(false);
    }
  };

  // ---------------------------------------------------------------- rendu

  if (chargement) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#246990] mx-auto" />
          <p className="mt-4 text-gray-600 text-sm">Chargement des employés…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">

      <div className="flex items-start gap-3 rounded-lg border border-[#246990]/30 bg-[#246990]/5 p-4">
        <Info size={18} className="mt-0.5 shrink-0 text-[#246990]" />
        <div className="text-sm text-gray-700 leading-relaxed">
          <p className="font-medium text-gray-900 mb-1">Ce que fait une dérogation</p>
          <p>
            Elle s'applique à <strong>une seule personne</strong>, par-dessus son groupe
            et ses rôles. <strong>Accorder</strong> ouvre un droit que son groupe ne
            donne pas ; <strong>refuser</strong> ferme un droit que son groupe donne — le
            seul moyen de dire « tout le groupe sauf lui ».
          </p>
          <p className="mt-1.5 text-gray-600">
            Un refus l'emporte sur tout. Une dérogation n'expire pas : elle reste jusqu'à
            ce que quelqu'un la retire.
          </p>
        </div>
      </div>

      {/* Les messages passent par `Toast` (CLAUDE.md). `erreur` ne sert plus
          qu'aux echecs de CHARGEMENT, qui doivent rester a l'ecran : un toast
          qui s'efface au bout de quatre secondes laisserait une page vide sans
          expliquer pourquoi. */}
      {erreur && (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{erreur}</span>
        </div>
      )}


      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* ---------------- Colonne employés ---------------- */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <UserCog size={18} className="text-[#246990]" />
              Employés
            </CardTitle>
            <CardDescription className="text-xs">
              {nombreConnectables} sur {employes.length} ont un accès à l'application.
              Une dérogation ne change rien pour les autres tant qu'aucun accès ne leur
              est ouvert.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative mb-3">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
                placeholder="Rechercher un employé…"
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#246990]/30 focus:border-[#246990]"
              />
            </div>

            {/* Le cas d'usage d'origine — preparer les droits avant d'ouvrir un
                compte — reste accessible, et l'interrupteur dit d'avance
                combien de lignes il ajoute. */}
            {employes.length > nombreConnectables && (
              <label className="mb-3 flex cursor-pointer items-center gap-2 text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={montrerSansAcces}
                  onChange={(e) => setMontrerSansAcces(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-[#246990] focus:ring-[#246990]"
                />
                Afficher aussi les {employes.length - nombreConnectables} employé(s) sans accès
              </label>
            )}

            <div className="max-h-[520px] overflow-y-auto space-y-1">
              {employesTries.map((e) => {
                const actif = employeChoisi?.id === e.id;
                const connectable = Boolean(e.identifiant || e.email);
                return (
                  <button
                    key={e.id}
                    onClick={() => choisirEmploye(e)}
                    className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${
                      actif
                        ? 'border-[#246990] bg-[#246990]/10'
                        : 'border-transparent hover:bg-gray-50'
                    }`}
                  >
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {`${e.prenom || ''} ${e.nom || ''}`.trim() || `Employé #${e.id}`}
                    </div>
                    <div className="text-xs text-gray-500 flex items-center gap-1.5">
                      {connectable
                        ? (e.identifiant || e.email)
                        : <span className="italic">sans accès à l'application</span>}
                    </div>
                  </button>
                );
              })}

              {employesTries.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-8">Aucun employé trouvé</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* ---------------- Colonne dérogations ---------------- */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle className="text-base">
                  {employeChoisi
                    ? `${employeChoisi.prenom || ''} ${employeChoisi.nom || ''}`.trim()
                    : 'Dérogations'}
                </CardTitle>
                <CardDescription className="text-xs">
                  {detail
                    ? `${detail.derogations.length} dérogation(s) · ${detail.acquis.length} droit(s) par groupe et rôles`
                    : 'Sélectionnez un employé à gauche'}
                </CardDescription>
              </div>
              {employeChoisi && (
                <Button
                  size="sm"
                  onClick={() => setDialogueOuvert(true)}
                  className="bg-[#246990] hover:bg-[#1d5674] text-white shrink-0"
                >
                  <Plus size={15} className="mr-1.5" />
                  Ajouter
                </Button>
              )}
            </div>
          </CardHeader>

          <CardContent>
            {!employeChoisi && (
              <div className="text-center py-16 text-gray-500">
                <UserCog size={44} className="mx-auto mb-3 text-gray-300" />
                <p className="text-sm">Choisissez un employé pour voir et poser ses dérogations</p>
              </div>
            )}

            {employeChoisi && chargementDetail && (
              <div className="text-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#246990] mx-auto" />
              </div>
            )}

            {employeChoisi && !chargementDetail && detail && (
              <>
                {(detail.employe.groupes.length > 0 || detail.employe.roles.length > 0) && (
                  <div className="flex flex-wrap gap-1.5 mb-4 pb-4 border-b border-gray-100">
                    {detail.employe.groupes.map((g) => (
                      <Badge key={`g${g.id}`} variant="outline" className="text-xs">
                        Groupe : {g.nom}
                      </Badge>
                    ))}
                    {detail.employe.roles.map((r) => (
                      <Badge key={`r${r.id}`} variant="outline" className="text-xs">
                        Rôle : {r.nom}
                      </Badge>
                    ))}
                  </div>
                )}

                {detail.derogations.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <ShieldCheck size={40} className="mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">Aucune dérogation</p>
                    <p className="text-xs mt-1">
                      Cet employé n'a que les droits de son groupe et de ses rôles.
                    </p>
                  </div>
                )}

                {/* Un vrai tableau : la cle, les badges et le motif se
                    suivaient dans une pile de cartes en flex, donc rien ne
                    tombait au meme endroit d'une ligne a l'autre. */}
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="w-12 px-5 py-3"></th>
                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                          Droit
                        </th>
                        <th className="w-28 px-5 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">
                          Sens
                        </th>
                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                          Motif
                        </th>
                        <th className="w-52 px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                          Posée par
                        </th>
                        <th className="w-[150px] px-5 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                          Retirer
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.derogations.map((d, index) => {
                        const accorde = d.type === 'accorder';
                        const redondante = accorde && acquises.has(d.key);
                        return (
                          <tr
                            key={d.id}
                            className={`border-b border-gray-100 last:border-b-0 ${
                              index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                            }`}
                          >
                            <td className="px-5 py-3.5">
                              <span className={accorde ? 'text-green-700' : 'text-red-700'}>
                                {accorde ? <ShieldCheck size={18} /> : <ShieldOff size={18} />}
                              </span>
                            </td>

                            <td className="px-5 py-3.5">
                              {/* Le libelle francais devant, la cle technique
                                  dessous : c'est elle qu'on epelle au support. */}
                              <div className="text-sm font-medium text-gray-900">
                                {d.description || d.key}
                              </div>
                              {d.description && (
                                <div className="font-mono text-[11px] text-gray-400 break-all">
                                  {d.key}
                                </div>
                              )}
                              {redondante && (
                                <Badge variant="outline" className="mt-1 text-xs text-amber-700 border-amber-300">
                                  Déjà donnée par le groupe
                                </Badge>
                              )}
                            </td>

                            <td className="px-5 py-3.5 text-center">
                              <Badge
                                className={`text-xs ${
                                  accorde
                                    ? 'bg-green-600 hover:bg-green-600'
                                    : 'bg-red-600 hover:bg-red-600'
                                } text-white`}
                              >
                                {LIBELLE_TYPE[d.type]}
                              </Badge>
                            </td>

                            <td className="px-5 py-3.5 text-sm text-gray-600">
                              {d.raison || <span className="text-xs text-gray-300">—</span>}
                            </td>

                            {/* L'auteur avait sa place dans le motif : « testt
                                — posée par l'employé #105 ». Un identifiant ne
                                se lit pas, et il brouillait le texte saisi. */}
                            <td className="px-5 py-3.5">
                              {d.pose_par_nom ? (
                                <div className="text-sm text-gray-700">{d.pose_par_nom}</div>
                              ) : d.pose_par_id ? (
                                <div className="text-sm text-gray-400">Compte supprimé</div>
                              ) : (
                                <span className="text-xs text-gray-300">—</span>
                              )}
                              {joliJour(d.created_at) && (
                                <div className="text-[11px] text-gray-400">
                                  {joliJour(d.created_at)}
                                </div>
                              )}
                            </td>

                            <td className="px-5 py-3.5 text-right">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setDerogationARetirer(d)}
                                className="w-[110px] justify-center gap-2 text-gray-600 hover:border-red-300 hover:text-red-700"
                              >
                                <Trash2 size={14} />
                                Retirer
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ---------------- Dialogue d'ajout ---------------- */}
      <Dialog open={dialogueOuvert} onOpenChange={setDialogueOuvert}>
        <DialogContent className="w-[95vw] max-w-5xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
              <ShieldCheck size={18} />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Poser une dérogation
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                Pour {`${employeChoisi?.prenom || ''} ${employeChoisi?.nom || ''}`.trim()} uniquement.
                Les modules hors licence de l'établissement ne sont pas proposés.
              </DialogDescription>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Permission
              </label>
              <div className="relative mb-2">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={rechercheCle}
                  onChange={(e) => setRechercheCle(e.target.value)}
                  placeholder="Filtrer les permissions…"
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#246990]/30"
                />
              </div>
              <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {permissionsProposables.map((p) => (
                  <button
                    key={p.key}
                    onClick={() => setCleChoisie(p.key)}
                    className={`w-full text-left px-3 py-2 text-xs transition-colors ${
                      cleChoisie === p.key ? 'bg-[#246990]/10' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="font-mono font-medium text-gray-900 break-all">{p.key}</div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {acquises.has(p.key) && (
                        <span className="text-[11px] text-amber-700">déjà acquise</span>
                      )}
                      {p.description && (
                        <span className="text-[11px] text-gray-500 truncate">{p.description}</span>
                      )}
                    </div>
                  </button>
                ))}
                {permissionsProposables.length === 0 && (
                  <p className="text-xs text-gray-500 text-center py-6">Aucune permission</p>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Sens</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setTypeChoisi('accorder')}
                    className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                      typeChoisi === 'accorder'
                        ? 'border-green-600 bg-green-50 text-green-800'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <ShieldCheck size={15} /> Accorder
                  </button>
                  <button
                    onClick={() => setTypeChoisi('refuser')}
                    className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                      typeChoisi === 'refuser'
                        ? 'border-red-600 bg-red-50 text-red-800'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <ShieldOff size={15} /> Refuser
                  </button>
                </div>
                <p className="text-[11px] text-gray-500 mt-1.5">
                  {typeChoisi === 'accorder'
                    ? "Ouvre ce droit à cette personne, même si son groupe ne le donne pas."
                    : "Ferme ce droit pour cette personne, même si son groupe le donne."}
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Motif <span className="text-red-600">*</span>
                </label>
                <textarea
                  value={motif}
                  onChange={(e) => setMotif(e.target.value)}
                  rows={4}
                  placeholder="Pourquoi cette exception ? Ce texte sera relu dans six mois."
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#246990]/30 resize-none"
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  Obligatoire — c'est la seule trace de la décision.
                </p>
              </div>

              {cleChoisie && (
                <div className="flex items-start gap-2 rounded-lg bg-gray-50 border border-gray-200 p-2.5">
                  <Lock size={13} className="mt-0.5 shrink-0 text-gray-400" />
                  <p className="text-[11px] text-gray-600 leading-relaxed">
                    <span className="font-mono break-all">{cleChoisie}</span> sera{' '}
                    {typeChoisi === 'accorder' ? 'accordée' : 'refusée'} à cette personne.
                    Prise en compte immédiate.
                  </p>
                </div>
              )}
            </div>
          </div>

          </div>

          <DialogFooter className="border-t bg-gray-50 px-5 py-4">
            <Button
              variant="outline"
              className={BOUTON_PIED_CLASSES}
              onClick={() => setDialogueOuvert(false)}
              disabled={enregistrement}
            >
              Annuler
            </Button>
            <Button
              onClick={enregistrer}
              disabled={!cleChoisie || motif.trim().length < 5 || enregistrement}
              className={`${BOUTON_PIED_CLASSES} bg-[#246990] text-white hover:bg-[#1d5674]`}
            >
              <ShieldCheck className="h-4 w-4" />
              {enregistrement ? 'Enregistrement…' : 'Poser la dérogation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CLAUDE.md : une action irreversible passe par `ConfirmDialog`. Retirer
          une derogation etait immediat, sans rien demander. */}
      <ConfirmDialog
        isOpen={Boolean(derogationARetirer)}
        onClose={() => setDerogationARetirer(null)}
        onConfirm={retirer}
        isLoading={retraitEnCours}
        type="warning"
        title="Retirer cette dérogation ?"
        description={
          derogationARetirer
            ? `${derogationARetirer.description || derogationARetirer.key} — ${
                derogationARetirer.type === 'accorder'
                  ? "ce droit accordé à titre personnel sera repris."
                  : "ce refus sera levé : la personne retrouvera le droit que lui donne son groupe."
              }`
            : ''
        }
        confirmText="Retirer"
      />

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
};

export default DerogationsManager;
